import { createContext, useContext, useEffect, useState } from 'react'

// Lightweight i18n foundation. Ships English + Hindi dictionaries for the
// site chrome (nav, CTA buttons, hero, footer). Pages translate their visible
// strings via useI18n() when they opt in; the provider also sets the lang
// attribute and persists the choice. New languages = new dictionary entries.

export const LANGS = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'hi', label: 'हिन्दी', flag: '🇮🇳' },
]

const DICTS = {
  en: {
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.pricing': 'Pricing',
    'nav.products': 'Products',
    'nav.demo': 'Live Demos',
    'nav.marketplace': 'Marketplace',
    'nav.company': 'Company',
    'nav.support': 'Support',
    'nav.account': 'Account',
    'nav.signin': 'Sign In',
    'nav.signup': 'Get Started',
    'nav.dashboard': 'Dashboard',
    'nav.blog': 'Blog',
    'nav.integrations': 'Integrations',
    'cta.talk_to_sales': 'Talk to sales',
    'cta.view_demo': 'View live demo',
    'cta.get_started': 'Get started',
    'cta.learn_more': 'Learn more',
    'hero.tagline': 'AI-native products, built across sectors.',
    'footer.tagline': 'OPRAIZ builds its own AI models and shapes them into products for media, devices, commerce, security, healthcare and government.',
    'footer.company': 'Company',
    'footer.products': 'Products',
    'footer.services': 'Services',
    'footer.legal': 'Legal',
    'footer.about': 'About Us',
    'footer.pricing': 'Pricing',
    'footer.changelog': 'Changelog',
    'footer.account': 'My Account',
    'footer.blog': 'Blog',
    'footer.integrations': 'Integrations',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms of Service',
    'footer.rights': 'All rights reserved.',
    'footer.industries': 'Industries',
    'footer.custom_ai': 'Custom AI Tools',
    'footer.rd_services': 'R&D Services',
    'footer.whitelabel': 'White-Label',
    'footer.deploy': 'Enterprise Deploy',
    'footer.saas': 'SaaS Development',
    'footer.sector': 'Sector-Agnostic Platforms',
    'footer.privacy_short': 'Privacy',
    'footer.terms_short': 'Terms',
    'footer.management': 'OPRAIZ Management',
    'common.view_all_products': 'View All Products',
    'common.products': 'Products',
    'common.pricing': 'Pricing',
    'common.search': 'Search',
    'common.search_label': 'Search the site',
    'common.search_placeholder': 'Search products, help, blog, marketplace…',
  },
  hi: {
    'nav.home': 'होम',
    'nav.services': 'सेवाएँ',
    'nav.about': 'हमारे बारे में',
    'nav.contact': 'संपर्क',
    'nav.pricing': 'मूल्य',
    'nav.products': 'उत्पाद',
    'nav.demo': 'लाइव डेमो',
    'nav.marketplace': 'बाजार',
    'nav.company': 'कंपनी',
    'nav.support': 'सहायता',
    'nav.account': 'खाता',
    'nav.signin': 'साइन इन',
    'nav.signup': 'शुरू करें',
    'nav.dashboard': 'डैशबोर्ड',
    'nav.blog': 'ब्लॉग',
    'nav.integrations': 'एकीकरण',
    'cta.talk_to_sales': 'बिक्री से बात करें',
    'cta.view_demo': 'लाइव डेमो देखें',
    'cta.get_started': 'शुरू करें',
    'cta.learn_more': 'अधिक जानें',
    'hero.tagline': 'AI-नेटिव प्रोडक्ट, कई क्षेत्रों में बनाए गए।',
    'footer.tagline': 'OPRAIZ अपने खुद के AI मॉडल बनाता है और उन्हें मीडिया, डिवाइस, कॉमर्स, सुरक्षा, हेल्थकेयर और गवर्नमेंट के लिए प्रोडक्ट में आकार देता है।',
    'footer.company': 'कंपनी',
    'footer.products': 'उत्पाद',
    'footer.services': 'सेवाएँ',
    'footer.legal': 'कानूनी',
    'footer.about': 'हमारे बारे में',
    'footer.pricing': 'मूल्य',
    'footer.changelog': 'परिवर्तन लॉग',
    'footer.account': 'मेरा खाता',
    'footer.blog': 'ब्लॉग',
    'footer.integrations': 'एकीकरण',
    'footer.privacy': 'गोपनीयता नीति',
    'footer.terms': 'सेवा की शर्तें',
    'footer.rights': 'सर्वाधिकार सुरक्षित।',
    'footer.industries': 'उद्योग',
    'footer.custom_ai': 'कस्टम AI टूल',
    'footer.rd_services': 'R&D सेवाएँ',
    'footer.whitelabel': 'व्हाइट-लेबल',
    'footer.deploy': 'एंटरप्राइज़ डिप्लॉय',
    'footer.saas': 'सास विकास',
    'footer.sector': 'सेक्टर-अज्ञेय प्लेटफ़ॉर्म',
    'footer.privacy_short': 'गोपनीयता',
    'footer.terms_short': 'शर्तें',
    'footer.management': 'OPRAIZ प्रबंधन',
    'common.view_all_products': 'सभी उत्पाद देखें',
    'common.products': 'उत्पाद',
    'common.pricing': 'मूल्य',
    'common.search': 'खोजें',
    'common.search_label': 'साइट खोजें',
    'common.search_placeholder': 'उत्पाद, सहायता, ब्लॉग, बाजार खोजें…',
  },
}

const KEY = 'opraiz.lang'

const I18nContext = createContext({ lang: 'en', t: (k) => DICTS.en[k] || k, setLang: () => {} })

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem(KEY) || (navigator.language?.startsWith('hi') ? 'hi' : 'en') } catch (e) { console.warn('[i18n] failed reading lang preference:', e); return 'en' }
  })

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang)
    try { localStorage.setItem(KEY, lang) } catch (e) { console.warn('[i18n] failed saving lang preference:', e) }
  }, [lang])

  const t = (key) => DICTS[lang]?.[key] || DICTS.en[key] || key

  return <I18nContext.Provider value={{ lang, t, setLang }}>{children}</I18nContext.Provider>
}

export function useI18n() {
  return useContext(I18nContext)
}